import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import JsonLd from "@/components/JsonLd";
import { SITE_URL, faqLd } from "@/lib/structuredData";
import {
  CHANGELOG,
  RELEASES,
  areaCounts,
  formatDay,
  releasesByDay,
  type Release,
} from "@/lib/releaseLog";

/**
 * /changelog — the release history, generated from git (see
 * scripts/build-changelog.mjs). Freshness signal for search, and the page
 * that answers "is this thing actually maintained?" without asking anyone
 * to take our word for it.
 *
 * Everything rendered here is derived. Do not hand-edit entries: fix the
 * commit convention or the NOISE/AREAS rules in the generator instead.
 */

const AREA_STYLES: Record<string, string> = {
  Automations: "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Funnel map": "bg-amber-50 text-amber-700 border-amber-200",
  Contacts: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Email: "bg-sky-50 text-sky-700 border-sky-200",
  "SMS & WhatsApp": "bg-teal-50 text-teal-700 border-teal-200",
  Appointments: "bg-rose-50 text-rose-700 border-rose-200",
  "AI & MCP": "bg-violet-50 text-violet-700 border-violet-200",
  Website: "bg-slate-100 text-slate-600 border-slate-200",
  Reliability: "bg-orange-50 text-orange-700 border-orange-200",
  Product: "bg-slate-100 text-slate-600 border-slate-200",
};

function areaClass(area: string) {
  return AREA_STYLES[area] ?? "bg-slate-100 text-slate-600 border-slate-200";
}

const FAQ = [
  {
    q: "How often does FrazzleCRM ship?",
    a: `${CHANGELOG.releaseCount} releases went out between ${formatDay(
      CHANGELOG.firstRelease
    )} and ${formatDay(CHANGELOG.latestRelease)} — ${
      CHANGELOG.shippingDays
    } days that had at least one release, averaging ${
      CHANGELOG.releasesPerShippingDay
    } releases on a shipping day. Every one is a real production deploy, not a sprint boundary.`,
  },
  {
    q: "Where does the FrazzleCRM changelog come from?",
    a: "It is generated from git history. Each entry is the subject line of a release commit on the develop branch, grouped by the image tag it shipped under. Nothing is written for the changelog after the fact, so it cannot flatter the work — it can only report it.",
  },
  {
    q: "Is anything left out of the changelog?",
    a: "One category: commits that only describe a push — re-tagging an image, rebuilding after a bad upload — are dropped, because they describe a deploy rather than a change to the product. Nothing else is filtered. No release is held back for being small, dull or embarrassing, which is why several of them are all three.",
  },
];

function ReleaseCard({ release }: { release: Release }) {
  return (
    <li className="relative rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-slate-900 px-2 py-0.5 font-mono text-[12px] font-semibold text-white">
          v{release.version}
        </span>
        {release.areas.map((a) => (
          <span
            key={a}
            className={`rounded-full border px-2 py-0.5 text-[11.5px] font-semibold ${areaClass(
              a
            )}`}
          >
            {a}
          </span>
        ))}
      </div>
      <ul className="mt-3 space-y-2">
        {release.entries.map((e) => (
          <li
            key={e}
            className="flex gap-2.5 text-[14.5px] leading-relaxed text-slate-700"
          >
            <span aria-hidden className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-300" />
            <span>{e}</span>
          </li>
        ))}
      </ul>
    </li>
  );
}

export default function ChangelogPage() {
  const areas = areaCounts();
  const [active, setActive] = React.useState<string | null>(null);

  const filtered = active
    ? RELEASES.filter((r) => r.areas.includes(active))
    : RELEASES;
  const days = releasesByDay(filtered);

  return (
    <SiteShell
      title={`Changelog — every FrazzleCRM release | ${CHANGELOG.releaseCount} shipped since ${formatDay(
        CHANGELOG.firstRelease
      )}`}
      description={`Every FrazzleCRM release, generated straight from git history: ${CHANGELOG.releaseCount} production deploys between ${formatDay(
        CHANGELOG.firstRelease
      )} and ${formatDay(
        CHANGELOG.latestRelease
      )}, with what changed in each one.`}
      canonical={`${SITE_URL}/changelog`}
      ogTitle={`${CHANGELOG.releaseCount} releases in ${CHANGELOG.spanDays} days — the FrazzleCRM changelog`}
    >
      <JsonLd data={faqLd(FAQ)} />

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-4xl px-6 pb-10 pt-14 sm:pt-16">
          <div className="mb-4 w-fit rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[12px] font-semibold text-indigo-700">
            Changelog · generated from git
          </div>
          <h1 className="text-[32px] font-extrabold leading-[1.15] tracking-tight text-slate-900 sm:text-[42px]">
            Every release, straight out of the commit log
          </h1>
          <p className="mt-4 max-w-2xl text-[15.5px] leading-relaxed text-slate-600">
            Nobody writes this page. A script reads the release commits on our
            develop branch and prints them, which means the boring fixes and the
            embarrassing ones are here next to the features. If you want to know
            whether a tool is actually being worked on, this is the honest
            version of that answer.
          </p>

          <dl className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "Releases", v: String(CHANGELOG.releaseCount) },
              { k: "Shipping days", v: String(CHANGELOG.shippingDays) },
              {
                k: "Per shipping day",
                v: String(CHANGELOG.releasesPerShippingDay),
              },
              { k: "Latest", v: formatDay(CHANGELOG.latestRelease) },
            ].map((s) => (
              <div
                key={s.k}
                className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm"
              >
                <dt className="text-[11.5px] font-semibold uppercase tracking-wide text-slate-400">
                  {s.k}
                </dt>
                <dd className="mt-1 text-[17px] font-bold text-slate-900">
                  {s.v}
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-[13px] leading-relaxed text-slate-500">
            First release {formatDay(CHANGELOG.firstRelease)}. The list is
            rebuilt from git as part of each release, so the newest entry is
            normally the version serving this page to you right now.
          </p>
        </div>
      </section>

      {/* Area filter. Deliberately not sticky at any width: ten chips wrap to
          four rows at 375px, and the site header already holds top-0 with a
          higher z-index, so a sticky bar would slide under it. */}
      <nav
        aria-label="Filter releases by area"
        className="border-y border-slate-100 bg-white"
      >
        <div className="mx-auto flex max-w-4xl flex-wrap gap-2 px-6 py-3">
          <button
            type="button"
            onClick={() => setActive(null)}
            aria-pressed={active === null}
            className={`inline-flex min-h-[44px] items-center rounded-full border px-4 text-[13px] font-semibold ${
              active === null
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-600"
            }`}
          >
            Everything
            <span className="ml-1.5 opacity-60">{RELEASES.length}</span>
          </button>
          {areas.map((a) => (
            <button
              key={a.area}
              type="button"
              onClick={() => setActive(active === a.area ? null : a.area)}
              aria-pressed={active === a.area}
              className={`inline-flex min-h-[44px] items-center rounded-full border px-4 text-[13px] font-semibold ${
                active === a.area
                  ? "border-slate-900 bg-slate-900 text-white"
                  : areaClass(a.area)
              }`}
            >
              {a.area}
              <span className="ml-1.5 opacity-60">{a.count}</span>
            </button>
          ))}
        </div>
      </nav>

      <section className="mx-auto max-w-4xl px-6 pb-16 pt-10">
        {days.map((day) => (
          <div key={day.date} className="mb-10">
            {/* Deliberately not sticky: the site header already owns top-0
                with a higher z-index, so a sticky day heading would slide
                under it and vanish. */}
            <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
              {formatDay(day.date)}
              <span className="ml-2 text-[13px] font-medium text-slate-400">
                {day.releases.length}{" "}
                {day.releases.length === 1 ? "release" : "releases"}
              </span>
            </h2>
            <ul className="mt-4 space-y-3">
              {day.releases.map((r) => (
                <ReleaseCard key={`${r.date}-${r.version}`} release={r} />
              ))}
            </ul>
          </div>
        ))}

        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-8 text-center">
          <h2 className="text-[22px] font-bold text-slate-900">
            Most of these came from someone hitting a wall
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-[14px] leading-relaxed text-slate-600">
            A reminder that fired after the appointment. A branch with nothing
            on the far side. Start free and the whole list is already in there.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <a
              href={`${APP_URL}/signup`}
              className="rounded-lg bg-indigo-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-indigo-500"
            >
              Start free
            </a>
            <Link
              href="/templates"
              className="rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-[14px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              Browse templates
            </Link>
          </div>
          <p className="mt-6 text-[13px] leading-relaxed text-slate-500">
            Curious how the release pace is possible?{" "}
            <Link
              href="/building-in-public"
              className="font-semibold text-indigo-600 hover:text-indigo-500"
            >
              We build in public
            </Link>{" "}
            and write up the hard parts on the{" "}
            <Link
              href="/blog"
              className="font-semibold text-indigo-600 hover:text-indigo-500"
            >
              engineering blog
            </Link>
            . New to the vocabulary? Start with the{" "}
            <Link
              href="/learn"
              className="font-semibold text-indigo-600 hover:text-indigo-500"
            >
              glossary
            </Link>
            .
          </p>
        </div>

        <div className="mt-12">
          <h2 className="text-[20px] font-bold tracking-tight text-slate-900">
            About this page
          </h2>
          <dl className="mt-4 space-y-5">
            {FAQ.map((f) => (
              <div key={f.q}>
                <dt className="text-[15px] font-semibold text-slate-900">
                  {f.q}
                </dt>
                <dd className="mt-1 text-[14px] leading-relaxed text-slate-600">
                  {f.a}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </SiteShell>
  );
}
